import { defineStore } from "pinia";
import { ref, computed } from "vue";
import LeadDashService from "@/services/crm/lead-dashboard.service";
import { usePipelineStore } from "@/stores/pipelineStore";
import { toast } from "@/utils/toast";
import { createScopedCache } from "@/stores/_cache";

const dashboardCache = createScopedCache("dashboard:leads");
const LEADS_DASHBOARD_TTL = 90 * 1000;

export const useLeadDashboardStore = defineStore("leadDashboard", () => {
  const loading = ref(false);
  const error = ref(null);

  const filters = ref({
    q: "",
    owner_id: null,
    pipeline_id: null,
    stage_id: null,
    tags: "",
    date_from: "",
    date_to: "",
  });

  // payload
  const kpis = ref({});
  const leadsByStage = ref([]);
  const leadsBySource = ref([]);
  const topCompanies = ref([]);
  const recentLeads = ref([]);
  const heatmap = ref({ series: [] });

  const pipelineStore = usePipelineStore();

  function safeArr(v) {
    return Array.isArray(v) ? v : [];
  }

  async function fetchDashboard(extra = {}, options = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      return await dashboardCache.get(params, {
        ttlMs: LEADS_DASHBOARD_TTL,
        force: options.force === true,
        loader: async () => {
          const { data } = await LeadDashService.get(params);
          return data || {};
        },
        onHydrate: (data) => {
          kpis.value = data?.kpis || {};
          leadsByStage.value = safeArr(data?.leads_by_stage);
          leadsBySource.value = safeArr(data?.leads_by_source);
          topCompanies.value = safeArr(data?.top_companies);
          recentLeads.value = safeArr(data?.recent_leads);
          heatmap.value = data?.heatmap || { series: [] };
        },
      });
    } catch (e) {
      const msg =
        e?.response?.data?.message || "Failed to load leads dashboard";
      error.value = msg;
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function invalidateDashboard() {
    dashboardCache.invalidate();
  }

  // same idea as Deals: map stage name -> stage_id using pipelineStore cache
  function setStageByName(stageName) {
    const name = String(stageName || "")
      .trim()
      .toLowerCase();
    if (!name) return false;

    const allPipes = pipelineStore.items || [];
    for (const p of allPipes) {
      const stages = Array.isArray(p.stages) ? p.stages : [];
      for (const s of stages) {
        if (
          String(s.name || "")
            .trim()
            .toLowerCase() === name
        ) {
          filters.value.stage_id = s.id || null;
          filters.value.pipeline_id = p.id || filters.value.pipeline_id;
          return true;
        }
      }
    }
    return false;
  }

  return {
    loading,
    error,
    filters,
    kpis,
    leadsByStage,
    leadsBySource,
    topCompanies,
    recentLeads,
    heatmap,
    fetchDashboard,
    invalidateDashboard,
    setStageByName,
  };
});
